import { useState, useEffect, useCallback } from 'react';

interface Phase {
  id: string;
  duration: number;
}

interface UseAnimationPhasesOptions<T extends Phase> {
  phases: readonly T[];
  autoStart?: boolean;
}

interface UseAnimationPhasesReturn<T extends Phase> {
  currentPhaseIndex: number;
  currentPhase: T;
  isAutoPlaying: boolean;
  isComplete: boolean;
  replay: () => void;
  skip: () => void;
  pause: () => void;
  resume: () => void;
}

export function useAnimationPhases<T extends Phase>({
  phases,
  autoStart = true,
}: UseAnimationPhasesOptions<T>): UseAnimationPhasesReturn<T> {
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoStart);

  useEffect(() => {
    if (!isAutoPlaying) return;

    if (currentPhaseIndex >= phases.length - 1) {
      setIsAutoPlaying(false);
      return;
    }

    const timer = setTimeout(() => {
      setCurrentPhaseIndex(prev => prev + 1);
    }, phases[currentPhaseIndex].duration);

    return () => clearTimeout(timer);
  }, [currentPhaseIndex, isAutoPlaying, phases]);

  const replay = useCallback(() => {
    setCurrentPhaseIndex(0);
    setIsAutoPlaying(true);
  }, []);

  const skip = useCallback(() => {
    setCurrentPhaseIndex(phases.length - 1);
    setIsAutoPlaying(false);
  }, [phases.length]);

  const pause = useCallback(() => {
    setIsAutoPlaying(false);
  }, []);

  const resume = useCallback(() => {
    setIsAutoPlaying(true);
  }, []);

  const currentPhase = phases[currentPhaseIndex];
  const isComplete = currentPhaseIndex === phases.length - 1 && !isAutoPlaying;

  return {
    currentPhaseIndex,
    currentPhase,
    isAutoPlaying,
    isComplete,
    replay,
    skip,
    pause,
    resume,
  };
}
